<template>
  <teleport to="body">
    <transition name="fade">
      <div
        v-if="showSearch"
        v-closeOnPressEsc="close"
        class="fixed top-0 left-0 p-15 w-full h-full bg-backdrop flex justify-center items-start z-100"
        @click.self="close"
      >
        <div
          class="relative py-20 bg-book rounded-6 w-full max-w-500 top-40 md:top-1/5"
          :class="isDarkMode ? 'shadow-search-dark' : 'shadow-dropdown'"
        >
          <div>
            <div class="relative">
              <label class="flex items-center border-3 border-app-color rounded-6 p-10 mx-20">
                <i class="icon-search mr-2 ml-5 font-bold text-app-color cursor-pointer" />
                <input
                  ref="inputRef"
                  v-model="searchValue"
                  type="text"
                  placeholder="Search"
                  class="w-full text-app-color bg-transparent pr-10 pl-5 focus:outline-none placeholder-app-color opacity-50 focus:opacity-100 anim-opacity"
                  @keydown="arrowNavigation"
                >
                <span class="flex-shrink-0 text-14 mr-5 font-semibold text-app-color opacity-30">ctrl + k</span>
              </label>
              <div v-if="filtered.length > 0" class="w-full max-h-355 overflow-y-auto mt-15 pt-1">
                <div v-if="!searchValue" class="px-20 pt-10 pb-2">
                  <span class="text-17 font-bold text-app-color">Recent</span>
                </div>

                <div
                  v-for="(item, index) in filtered"
                  :key="item.id + item.title + index"
                  :ref="el => indexRefs[index] = el"
                  class="search flex justify-between items-center px-20 py-9 mx-20 bg-transparent cursor-pointer group mb-10 rounded-6 group"
                  :class="[
                    activeItemClass(index),
                    searchValue ? item.label === 'book' ? 'search-books' : 'search-pages' : ''
                  ]"
                  @mousedown.prevent.stop
                  @click="select(item)"
                >
                  <BookLabel v-if="item.label === 'book'" background="bg-project" class="py-4" :book="item" />

                  <div v-else class="flex">
                    <img v-if="item.icon" class="w-20 h-20 mr-10" :src="require(`@/assets/images/icons/${item.icon}.png`)" alt="icon">
                    <img v-else-if="item.icon === null" class="w-20 h-20 mr-10" src="@/assets/images/icons/purple_circle.png" alt="icon">
                    <img v-else class="w-20 h-20 mr-10" src="@/assets/images/planet.svg" alt="icon">

                    <div class="leading-tight">
                      <p class="text-app-color font-bold truncate">{{ item.title }}</p>
                      <p v-if="item.book" class="text-app-color truncate text-12 mt-2">{{ item.book.title }}</p>
                    </div>
                  </div>
                  <div
                    class="rotate-icon opacity-0 group-hover:opacity-100 anim-opacity"
                    :class="{'opacity-100': index === activeIndex}"
                  >
                    <i class="icon-arrow-up text-19 font-bold text-dark-600" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
import { defineComponent, computed, ref, nextTick, onMounted, onBeforeUpdate, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from 'vuex'

import localStorageService from '@/core/local-storage'

import BookLabel from '@/components/books/BookLabel'

export default defineComponent({
  name: 'SearchModal',

  components: { BookLabel },

  setup () {
    const { state, getters, dispatch, commit } = useStore()
    const router = useRouter()

    const indexRefs = ref([])
    const inputRef = ref(null)

    const showSearch = computed(() => state.general.showSearch)
    const searchValue = ref('')
    const activeIndex = ref(0)

    const isTeamAccount = computed(() => getters.isTeamAccount)
    const isDarkMode = computed(() => getters.isDarkMode)
    const isMobile = computed(() => getters.isMobile)

    const books = computed(() => state.book.books)
    const projects = computed(() => state.project.projects)
    const projectsWereUpdated = computed(() => state.project.projectsWereUpdated)
    const user = computed(() => state.profile.user)
    const team = computed(() => state.team.team)
    const headquarters = computed(() => state.team.headquarters)

    const activeItemClass = computed(() => index => {
      const prefix = index === activeIndex.value ? '' : 'hover:'
      const shadow = index === activeIndex.value ? 'shadow-light' : 'shadow-event'

      return isDarkMode.value ? `${prefix}bg-selection shadow-event-dark` : `${prefix}bg-grey-1100 ${shadow}`
    })

    const title = computed(() => {
      return !searchValue.value && filtered.value.length ? 'Recent' : 'Pages and content'
    })

    const items = computed(() => {
      const bks = books.value.filter(item => !item.isSection).map(item => ({ ...item, label: 'book' }))
      return [...bks, ...projects.value]
    })

    const recentOpened = computed(() => {
      const userName = user.value.profile?.userName
      const allRecentFiles = localStorageService.getItem('recent-opened-items') || {}
      // get a recent opened an user projects
      const arr = allRecentFiles[userName] || []
      return arr.filter(item => items.value.some(i => i.id === item.id))
    })

    const filtered = computed(() => {
      if (!searchValue.value) return recentOpened.value

      return items.value
        .filter(item => item.title.toLowerCase().match(searchValue.value.toLowerCase()))
        .sort((a, b) => {
          if (a.label > b.label) return 1
          return -1
        })
    })

    onMounted(async () => {
      if (projectsWereUpdated.value) await dispatch('getProjects')
    })

    // to reset the refs before each update
    onBeforeUpdate(() => {
      indexRefs.value = []
    })

    watch(showSearch, (value) => {
      if (value) nextTick(() => inputRef.value.focus())
    })

    function close () {
      activeIndex.value = 0
      searchValue.value = ''
      commit('toggleSearch', false)
    }

    function arrowNavigation (e) {
      if (filtered.value.length) {
        if (e.keyCode === 38 && activeIndex.value > 0) {
          e.preventDefault()
          activeIndex.value--
          indexRefs.value[activeIndex.value].scrollIntoView(false)
        }
        if (e.keyCode === 40 && activeIndex.value < filtered.value.length - 1) {
          e.preventDefault()
          activeIndex.value++
          indexRefs.value[activeIndex.value].scrollIntoView(false)
        }
        if (e.keyCode === 13) {
          e.preventDefault()
          select(filtered.value[activeIndex.value])
        }
      }
    }

    function select (item) {
      activeIndex.value = 0
      searchValue.value = ''
      commit('toggleSearch', false)

      if (item.book?.id === headquarters.value?.id) {
        router.push({ name: 'Headquarters', query: { page: item.id } })
      } else {
        const path = isTeamAccount.value ? `/${team.value.link}` : ''

        router.push(item.book ? `${path}/space/${item.book.id}?page=${item.id}` : `${path}/space/${item.id}`).catch(() => {})
      }

      recentFiles(item)

      if (isMobile.value) commit('updateSidebar', false)
    }

    function recentFiles (item) {
      const allRecentOpened = localStorageService.getItem('recent-opened-items') || {}
      const userRecentOpened = allRecentOpened[user.value.profile?.userName] || []

      if (userRecentOpened.length > 3) {
        userRecentOpened.shift()
      }

      const isItemInRecentOpened = userRecentOpened.some(recentOpenedItem => recentOpenedItem.id === item.id)
      !isItemInRecentOpened && userRecentOpened.unshift(item)

      allRecentOpened[user.value.profile?.userName] = userRecentOpened
      localStorageService.setItem('recent-opened-items', allRecentOpened)
    }

    return {
      // refs
      inputRef,
      indexRefs,
      // data
      showSearch,
      searchValue,
      activeIndex,
      // computed
      isTeamAccount,
      isDarkMode,
      books,
      projects,
      projectsWereUpdated,
      user,
      team,
      headquarters,
      items,
      filtered,
      title,
      recentOpened,
      activeItemClass,
      // function
      close,
      arrowNavigation,
      select,
      recentFiles
    }
  }
})
</script>

<style scoped>
.rotate-icon {
  transform: rotateY(180deg) rotate(270deg);
}
</style>
